import { Injectable, OnDestroy, inject } from '@angular/core';

import { NotificationSeverity } from '@nexweb/shared/data-access/mdd';
import { NotificationDisplayOptions, NotificationService } from '@nexweb/shared/notification-helper';

/* eslint-disable @typescript-eslint/naming-convention -- payload keys mirror the backend PascalCase contract */
export interface UserAlertPayload {
  Severity: NotificationSeverity;
  TitleKey?: string;
  Title?: string;
  MessageKey?: string;
  Message?: string;
  MessageArgs?: string[];
  Life?: number;
  Closable?: boolean;
  Sticky?: boolean;
  /**
   * Generic, opt-in coalescing: when set, this payload is buffered under this key for
   * `CoalesceWindowMs` instead of displaying immediately, merging with any other payload sharing the
   * same key that arrives within the window. Producers should namespace their own keys (e.g. prefix
   * with a module name) so two unrelated producers' keys can never collide under
   * `DiscardCoalesceKeyPrefix`'s prefix matching. Left unset, a payload displays immediately — the path
   * every existing producer (e.g. EAO) takes today, unaffected by this field's existence.
   */
  CoalesceKey?: string;
  /**
   * Buffer duration in milliseconds for `CoalesceKey`. Required whenever `CoalesceKey` is set; if
   * missing, the payload is displayed immediately rather than buffered indefinitely (fail-safe).
   */
  CoalesceWindowMs?: number;
  /**
   * The `MessageKey` to substitute if, when a `CoalesceKey` buffer's window elapses, more than one
   * payload was buffered under it. By convention, on merge `MessageArgs[2]` becomes the buffered-payload
   * count and `MessageArgs[4]` becomes the maximum across all buffered payloads' `MessageArgs[4]` —
   * producers populating `CoalesceKey` should lay their `MessageArgs` out accordingly.
   */
  CoalescedMessageKey?: string;
  /**
   * When set on an incoming payload (typically a terminal/summary event for a group of coalesceable
   * payloads), any live `CoalesceKey` buffers whose key starts with this prefix are discarded — without
   * displaying — before this payload is shown.
   */
  DiscardCoalesceKeyPrefix?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

const COALESCE_COUNT_ARG_INDEX = 2;
const COALESCE_PROGRESS_ARG_INDEX = 4;

interface CoalesceBuffer {
  payloads: UserAlertPayload[];
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Renders the UserAlert notification lane. The backend sends intent + severity + message and leaves the
 * presentation to the client; this web client shows it as a toast. Kept separate from the hub so the hub
 * stays a thin router (mirrors SystemNotificationService / ErrorNotificationService).
 *
 * Also implements generic, payload-driven coalescing (buffer/merge/discard) via the optional
 * CoalesceKey/CoalesceWindowMs/CoalescedMessageKey/DiscardCoalesceKeyPrefix fields above. This lives
 * here — rather than in a domain-owned service behind a registration mechanism — so it's active in
 * every app automatically, exactly like every other producer's (e.g. EAO's) payloads, with no per-app
 * wiring of any kind.
 */
@Injectable({
  providedIn: 'root',
})
export class UserAlertService implements OnDestroy {
  private notificationService = inject(NotificationService);
  private buffers = new Map<string, CoalesceBuffer>();

  public show(payload: UserAlertPayload): void {
    if (payload.DiscardCoalesceKeyPrefix) {
      this.discardBuffersWithPrefix(payload.DiscardCoalesceKeyPrefix);
    }

    if (payload.CoalesceKey) {
      if (payload.CoalesceWindowMs == null) {
         
        console.warn(
          `UserAlertService: payload has CoalesceKey "${payload.CoalesceKey}" but no CoalesceWindowMs - displaying immediately instead of buffering indefinitely.`,
        );
      } else {
        this.buffer(payload.CoalesceKey, payload, payload.CoalesceWindowMs);

        return;
      }
    }

    this.displayImmediately(payload);
  }

  public ngOnDestroy(): void {
    for (const buffer of this.buffers.values()) {
      clearTimeout(buffer.timer);
    }
    this.buffers.clear();
  }

  private buffer(coalesceKey: string, payload: UserAlertPayload, coalesceWindowMs: number): void {
    const existing = this.buffers.get(coalesceKey);

    if (existing) {
      existing.payloads.push(payload);

      return;
    }

    const timer = setTimeout(() => this.flush(coalesceKey), coalesceWindowMs);
    this.buffers.set(coalesceKey, { payloads: [payload], timer });
  }

  private flush(coalesceKey: string): void {
    const buffer = this.buffers.get(coalesceKey);
    this.buffers.delete(coalesceKey);

    if (!buffer) {
      return;
    }

    this.displayImmediately(
      buffer.payloads.length === 1 ? buffer.payloads[0] : this.mergeBufferedPayloads(buffer.payloads),
    );
  }

  private mergeBufferedPayloads(payloads: UserAlertPayload[]): UserAlertPayload {
    const last = payloads[payloads.length - 1];
    const mergedArgs = [...(last.MessageArgs ?? [])];
    const highestProgress = Math.max(
      ...payloads.map((p) => Number(p.MessageArgs?.[COALESCE_PROGRESS_ARG_INDEX] ?? 0)),
    );

    mergedArgs[COALESCE_COUNT_ARG_INDEX] = `${payloads.length}`;
    mergedArgs[COALESCE_PROGRESS_ARG_INDEX] = `${highestProgress}`;

    /* eslint-disable @typescript-eslint/naming-convention -- payload keys mirror the backend PascalCase contract */
    return {
      ...last,
      MessageKey: last.CoalescedMessageKey ?? last.MessageKey,
      MessageArgs: mergedArgs,
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  private discardBuffersWithPrefix(prefix: string): void {
    for (const [key, buffer] of this.buffers) {
      if (key.startsWith(prefix)) {
        clearTimeout(buffer.timer);
        this.buffers.delete(key);
      }
    }
  }

  private displayImmediately(payload: UserAlertPayload): void {
    // Prefer a localization key; fall back to already-resolved literal text. NotificationService owns
    // the localization and the mapping onto the underlying toaster.
    const message = payload.MessageKey ?? payload.Message ?? '';
    const options: NotificationDisplayOptions = {
      title: payload.Title,
      titleKey: payload.TitleKey,
      messageArgs: payload.MessageArgs,
      literal: !payload.MessageKey,
      life: payload.Life,
    };

    switch (payload.Severity) {
      case NotificationSeverity.info:
        this.notificationService.info(message, payload.Sticky, payload.Closable, options);
        break;
      case NotificationSeverity.success:
        this.notificationService.success(message, payload.Sticky, payload.Closable, options);
        break;
      case NotificationSeverity.warning:
        this.notificationService.warn(message, payload.Sticky, payload.Closable, options);
        break;
      case NotificationSeverity.error:
        this.notificationService.error(message, payload.Sticky, payload.Closable, options);
        break;
      default:
        break;
    }
  }
}
